import { useContext, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'

import { DragIndicator } from '@mui/icons-material'
import { Box, Checkbox, FormControlLabel } from '@mui/material'

import {
  attachClosestEdge,
  extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge'
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview'
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview'

import { getItemData, isItemData, ListContext } from './utils'

const idleState = { type: 'idle' }
const draggingState = { type: 'dragging' }

const listItemContainerStyles = {
  position: 'relative',
  borderWidth: 0,
  borderBottomWidth: 1,
  borderStyle: 'solid',
  borderColor: '#f1f1f1',
  ':last-of-type': {
    borderWidth: 0,
  },
}

const listItemPreviewStyles = {
  px: 1.5,
  py: 0.2,
  borderRadius: 2,
  backgroundColor: 'white',
  maxWidth: '360px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}

export default function MetricsSelectorListItem({
  item,
  index,
  checked,
  onCheckChange,
}) {
  const { registerItem, instanceId } = useContext(ListContext)

  const ref = useRef(null)
  const dragHandleRef = useRef(null)

  const [draggableState, setDraggableState] = useState(idleState)
  const [closestEdge, setClosestEdge] = useState(null)

  useEffect(() => {
    const element = ref.current
    const dragHandle = dragHandleRef.current

    const data = getItemData({ item, index, instanceId })

    function onChange({ source, self }) {
      const isSource = source.element === dragHandle
      if (isSource) {
        setClosestEdge(null)
        return
      }

      const itemClosestEdge = extractClosestEdge(self.data)

      const sourceIndex = source.data.index

      const isItemBeforeSource = index === sourceIndex - 1
      const isItemAfterSource = index === sourceIndex + 1

      const isDropIndicatorHidden =
        (isItemBeforeSource && itemClosestEdge === 'bottom') ||
        (isItemAfterSource && itemClosestEdge === 'top')

      if (isDropIndicatorHidden) {
        setClosestEdge(null)
        return
      }

      setClosestEdge(itemClosestEdge)
    }

    return combine(
      registerItem({ itemId: item.apiValue, element }),
      draggable({
        element,
        dragHandle: dragHandleRef.current,
        getInitialData: () => data,
        onGenerateDragPreview({ nativeSetDragImage }) {
          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: pointerOutsideOfPreview({ x: '16px', y: '8px' }),
            render({ container }) {
              setDraggableState({ type: 'preview', container })

              return () => setDraggableState(draggingState)
            },
          })
        },
        onDragStart() {
          setDraggableState(draggingState)
        },
        onDrop() {
          setDraggableState(idleState)
        },
      }),
      dropTargetForElements({
        element,
        canDrop({ source }) {
          return isItemData(source.data) && source.data.instanceId === instanceId
        },
        getData({ input }) {
          return attachClosestEdge(data, {
            element,
            input,
            allowedEdges: ['top', 'bottom'],
          })
        },
        onDragEnter: onChange,
        onDrag: onChange,
        onDragLeave() {
          setClosestEdge(null)
        },
        onDrop() {
          setClosestEdge(null)
        },
      }),
    )
  }, [instanceId, item, index, registerItem])

  return (
    <>
      <Box ref={ref} sx={listItemContainerStyles}>
        <Box
          display="flex"
          alignItems="center"
          gap={1.5}
          sx={{
            position: 'relative',
            ...(draggableState.type === 'dragging' ? { opacity: 0.4 } : {}),
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            ref={dragHandleRef}
            sx={{
              backgroundColor: '#f1f2f4',
              p: 0.6,
              ml: 0.5,
              borderRadius: 1,
              '&:hover': { backgroundColor: '#dcdfe4', cursor: 'grab' },
              '&:active': { backgroundColor: '#b3b9c4', cursor: 'grabbing' },
            }}
          >
            <DragIndicator sx={{ fontSize: 14 }} />
          </Box>
          <FormControlLabel
            key={item.apiValue}
            slotProps={{ typography: { fontSize: 14 } }}
            control={
              <Checkbox checked={checked} size="small" onChange={onCheckChange} />
            }
            label={item.name}
          />
        </Box>
        {closestEdge && <DropIndicator edge={closestEdge} gap="1px" />}
      </Box>
      {draggableState.type === 'preview' &&
        ReactDOM.createPortal(
          <Box sx={listItemPreviewStyles}>{item.name}</Box>,
          draggableState.container,
        )}
    </>
  )
}
